// Shared number/date formatting for the stock details page.
//
// Numbers stay en-US (the screener and the rest of the app use en-US digits),
// dates follow the UI language. Every formatter returns DASH for missing,
// non-numeric or infinite input so templates never print NaN, "undefined"
// or float noise like 8.723299999999998.

export const DASH = "—";

export function isNum(v) {
  if (v === null || v === undefined || v === "" || typeof v === "boolean") return false;
  const n = Number(v);
  return !isNaN(n) && isFinite(n);
}

/** Grouped number with a fixed number of decimals: 1,234.50 */
export function num(v, decimals = 2) {
  if (!isNum(v)) return DASH;
  return Number(v).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** Share price: 2 decimals, 4 for sub-dollar prices. */
export function price(v) {
  if (!isNum(v)) return DASH;
  const abs = Math.abs(Number(v));
  return num(v, abs > 0 && abs < 1 ? 4 : 2);
}

/** Dollar amount: $341.39 */
export function money(v, decimals) {
  if (!isNum(v)) return DASH;
  const n = Number(v);
  const body = decimals === undefined ? price(Math.abs(n)) : num(Math.abs(n), decimals);
  return `${n < 0 ? "-" : ""}$${body}`;
}

const UNITS = [
  { v: 1e12, s: "T" },
  { v: 1e9, s: "B" },
  { v: 1e6, s: "M" },
  { v: 1e3, s: "K" },
];

/** Compact magnitude: 4.95T, 115.21B, 49.17M, 166K. */
export function compact(v, decimals = 2) {
  if (!isNum(v)) return DASH;
  const n = Number(v);
  const abs = Math.abs(n);
  const unit = UNITS.find((u) => abs >= u.v);
  if (!unit) return num(n, abs < 10 ? decimals : 0);
  return `${(n / unit.v).toFixed(decimals)}${unit.s}`;
}

/** Compact dollar amount: $4.95T */
export function moneyCompact(v, decimals = 2) {
  if (!isNum(v)) return DASH;
  const n = Number(v);
  return `${n < 0 ? "-" : ""}$${compact(Math.abs(n), decimals)}`;
}

/** Percentage from a value already expressed in percent (48.65 -> 48.65%). */
export function pct(v, decimals = 2, { signed = false } = {}) {
  if (!isNum(v)) return DASH;
  const n = Number(v);
  const sign = signed && n > 0 ? "+" : "";
  return `${sign}${num(n, decimals)}%`;
}

/** Signed number: +2.41 / -0.53 */
export function signed(v, decimals = 2) {
  if (!isNum(v)) return DASH;
  const n = Number(v);
  return `${n > 0 ? "+" : ""}${num(n, decimals)}`;
}

/** "up" | "down" | "flat" for colouring a value. */
export function tone(v) {
  if (!isNum(v)) return "flat";
  const n = Number(v);
  if (n > 0) return "up";
  if (n < 0) return "down";
  return "flat";
}

function intlLocale(locale) {
  return locale === "fr" ? "fr-CA" : "en-US";
}

/**
 * Parse an API date. Date-only strings ("2026-10-28" or "...T00:00:00Z")
 * are read as calendar dates so they never shift a day in western timezones.
 */
function toCalendarDate(v) {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  const s = String(v);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.0+)?Z?)?$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  return isNaN(d) ? null : d;
}

/** Calendar date in the UI language: "Oct 28, 2026" / "28 oct. 2026". */
export function date(v, locale = "en", options = { year: "numeric", month: "short", day: "numeric" }) {
  const d = toCalendarDate(v);
  if (!d) return DASH;
  return new Intl.DateTimeFormat(intlLocale(locale), options).format(d);
}

/** Quote timestamp (unix seconds) shown in exchange time: "Sep 22, 3:53 PM EDT". */
export function marketTime(unixSeconds, locale = "en") {
  if (!isNum(unixSeconds) || Number(unixSeconds) <= 0) return DASH;
  return new Intl.DateTimeFormat(intlLocale(locale), {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/New_York",
    timeZoneName: "short",
  }).format(new Date(Number(unixSeconds) * 1000));
}

/** Position (0-100) of `value` between `low` and `high`, clamped. */
export function rangePosition(value, low, high) {
  if (!isNum(value) || !isNum(low) || !isNum(high) || Number(high) <= Number(low)) return null;
  const p = ((Number(value) - Number(low)) / (Number(high) - Number(low))) * 100;
  return Math.max(0, Math.min(100, p));
}

/**
 * Number typed by a user in a filter field, or null when it is empty or not a
 * number. Accepts "1500", "1,500", "1 500", "1.5", "-10" and the French decimal
 * comma "1,5". In French a comma is always the decimal separator (thousands use
 * spaces); in English "1,500" is a thousands separator but "1,5" is a decimal.
 * A K / M / B / T suffix scales the number ("50B", "1,5M"); "Md" (milliard) is
 * accepted as B.
 */
const NUMBER_SUFFIXES = { k: 1e3, m: 1e6, b: 1e9, md: 1e9, t: 1e12 };

export function parseUserNumber(raw, locale = "en") {
  let s = String(raw ?? "").replace(/\s/g, "");
  if (!s) return null;
  let scale = 1;
  const suffix = s.match(/(md|[kmbt])$/i);
  if (suffix) {
    scale = NUMBER_SUFFIXES[suffix[1].toLowerCase()];
    s = s.slice(0, -suffix[1].length);
  }
  if (s.includes(",") && s.includes(".")) {
    s = s.replace(/,/g, "");
  } else if (s.includes(",")) {
    const thousands = locale !== "fr" && /^-?\d{1,3}(,\d{3})+$/.test(s);
    s = thousands ? s.replace(/,/g, "") : s.replace(",", ".");
  }
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(s)) return null;
  // Rounded so "1.1M" is 1100000, not 1100000.0000000002.
  const n = scale === 1 ? Number(s) : Math.round(Number(s) * scale * 1e4) / 1e4;
  return isFinite(n) ? n : null;
}

const COMPACT_UNITS = [[1e12, "T"], [1e9, "B"], [1e6, "M"]];

/**
 * A filter number shown back in the field in the UI language: 1,500.5 / 1 500,5.
 * Round millions and up keep the short form the user can type: 50B, 1.5M.
 */
export function formatUserNumber(n, locale = "en") {
  if (!isNum(n)) return "";
  const v = Number(n);
  for (const [unit, suffix] of COMPACT_UNITS) {
    const thousandths = (v / unit) * 1000;
    if (Math.abs(v) >= unit && Math.abs(thousandths - Math.round(thousandths)) < 1e-6) {
      return `${(v / unit).toLocaleString(intlLocale(locale), { maximumFractionDigits: 3 })}${suffix}`;
    }
  }
  return v.toLocaleString(intlLocale(locale), { maximumFractionDigits: 4 });
}

/** Vue mixin exposing the formatters to templates as fmt.* */
export const formatMixin = {
  computed: {
    fmt() {
      const locale = this.$i18n ? this.$i18n.locale : "en";
      return {
        DASH,
        num,
        price,
        money,
        compact,
        moneyCompact,
        pct,
        signed,
        tone,
        rangePosition,
        date: (v, opts) => date(v, locale, opts),
        marketTime: (v) => marketTime(v, locale),
      };
    },
  },
};
